import { useEffect, useRef } from 'react';
import { AnnotationMode } from 'pdfjs-dist';

/**
 * PageCanvas — Renders a single PDF page onto a <canvas>.
 * Accounts for devicePixelRatio for crisp rendering on HiDPI displays.
 * Clears the canvas buffer before each render to free memory on page changes.
 *
 * When `hideFormFields` is true, form widget annotations are not rendered
 * on the canvas — the caller is expected to render them as interactive
 * HTML overlays instead (see FormFieldOverlay).
 *
 * When `annotationCanvasMap` is provided, widget + freetext annotations are
 * diverted to per-annotation canvases stored in the map (instead of being
 * baked into the main page canvas). PdfAnnotationLayer then inserts those
 * canvases into its DOM section elements. `onRenderComplete` fires after
 * the page render finishes — used to gate PdfAnnotationLayer's render so it
 * consumes a freshly-populated map.
 */
export default function PageCanvas({
  pdfDoc,
  pageNumber,
  scale,
  className,
  onClick,
  hideFormFields,
  annotationCanvasMap,
  onRenderComplete,
}) {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    let cancelled = false;

    // Cancel any in-progress render immediately
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    // Clear the old canvas buffer before fetching the new page
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    pdfDoc.getPage(pageNumber).then((page) => {
      if (cancelled) return;
      if (!canvasRef.current) return;

      const dpr = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale });

      const displayWidth = viewport.width;
      const displayHeight = viewport.height;

      canvas.width = Math.floor(displayWidth * dpr);
      canvas.height = Math.floor(displayHeight * dpr);
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      const renderCtx = canvas.getContext('2d');
      renderCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const renderParams = { canvasContext: renderCtx, viewport };

      // We render form widgets ourselves via PdfAnnotationLayer (DOM inputs).
      // Use ENABLE_FORMS (not ENABLE) so PDF.js paints the page + non-form
      // annotations but SKIPS painting form-widget /AP onto the canvas. With
      // plain ENABLE, a *filled* widget's value is painted on the canvas AND
      // shown in the DOM <input>, slightly offset — the "double-baked" form
      // ghost (the saved bytes are clean; this is a render-only double).
      // Genuine hasOwnCanvas widgets (read-only / signature) still divert into
      // annotationCanvasMap below; ENABLE_FORMS preserves that. Do NOT revert
      // to ENABLE — verified via canvas-pixel measurement (1988 painted px ->
      // 0) on a filled genuine AcroForm.
      if (hideFormFields) {
        renderParams.annotationMode = AnnotationMode.ENABLE_FORMS;
      }

      // When a shared annotationCanvasMap is provided, widget + freetext
      // appearance streams get diverted from the main canvas into the map
      // (one canvas per annotation). PdfAnnotationLayer consumes the map
      // and inserts each canvas into the matching DOM section, so the
      // yellow paint appears inside the interactive DOM element instead
      // of on the page canvas.
      if (annotationCanvasMap) {
        // Reset the map for this render — page.render() populates it as a
        // side-effect of the canvas operator stream.
        annotationCanvasMap.clear();
        renderParams.annotationCanvasMap = annotationCanvasMap;
      }

      const renderTask = page.render(renderParams);
      renderTaskRef.current = renderTask;

      renderTask.promise.then(
        () => {
          if (cancelled) return;
          if (onRenderComplete) onRenderComplete();
        },
        (err) => {
          if (err.name !== 'RenderingCancelled' && err.name !== 'RenderingCancelledException') {
            console.error(`Error rendering page ${pageNumber}:`, err);
          }
        }
      );
    });

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, pageNumber, scale, hideFormFields, annotationCanvasMap, onRenderComplete]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      onClick={onClick}
      style={{ display: 'block' }}
    />
  );
}
